import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Typography,
  Container,
  TextField,
  Button,
  FormControl,
  FormLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { MdAddCall, MdEmail, MdLocationOn } from "react-icons/md";
import rentbanner from "@assets/images/istockphoto-3.jpg";
import image from "@assets/images/1.png";
import { toast } from "react-toastify";
import HowItWorks from "./HowItWorks";
import PropertyImageGallery from "./PropertyImageGallery";
import TestimonialsSection from "./TestimonialsSection";
const DESCO_API_BASE = import.meta.env.VITE_DESCO_API_BASE;
import SEO from "../../../utils/SeoComponent";

// Styled Components
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
}));

const HeroSection = styled(PageSection)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${rentbanner})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  color: "white",
  padding: theme.spacing(15, 0),
  textAlign: "center",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "150px",
    background: "linear-gradient(to top, #fff, transparent)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: "relative",
  fontWeight: 700,
  marginBottom: theme.spacing(6),
  paddingBottom: theme.spacing(2),
  display: "inline-block",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "80px",
    height: "4px",
    backgroundColor: "#C10E14",
  },
}));

const FormWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "15px",
  padding: theme.spacing(5),
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
  height: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },
}));

const InfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f9fc",
  borderRadius: "15px",
  padding: theme.spacing(5),
  height: "100%",
  boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
  backgroundColor: "#f8f9fc",
  borderRadius: "50%",
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  color: "#C10E14",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
}));

const PropertyImage = styled("img")(({ theme }) => ({
  width: "100%",
  borderRadius: "15px",
  boxShadow: "0 15px 35px rgba(0, 0, 0, 0.2)",
  marginBottom: theme.spacing(4),
}));

const ListProperty = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    propertyType: "",
    propertyAddress: "",
    message: "",
    propertyImage: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      propertyImage: e.target.files[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.name);
      formDataToSend.append("property_type", formData.propertyType);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("address", formData.propertyAddress);
      formDataToSend.append("message", formData.message);
      formDataToSend.append("image", formData.propertyImage);

      const response = await axios.post(
        `${DESCO_API_BASE}/properties/sale_property`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("Property listing submitted successfully!");

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        propertyType: "",
        propertyAddress: "",
        message: "",
        propertyImage: null,
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong!");
    }
  };

  return (
    <>
      <SEO slug={"listwithus"}  />
      <Box className="listproperty-page">
        {/* Hero Section */}
        <HeroSection className="hero-section">
          <Container
            maxWidth="md"
            sx={{
              mt: { xs: 6, md: 14 },
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                fontWeight={700}
              >
                Sell or Rent Out Your Property in Dubai
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  const element = document.getElementById("list-form");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                sx={{
                  borderRadius: "50px",
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
                  backgroundColor: "#C10E14", // Set the background color to #C10E14
                  "&:hover": {
                    backgroundColor: "#A30B11", // Set the hover background color to #A30B11
                  },
                }}
              >
                List Your Property
              </Button>
            </motion.div>
          </Container>
        </HeroSection>

        {/* Why List Section */}
        <PageSection sx={{ bgcolor: "#f8f9fc" }}>
          <Container>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <SectionTitle variant="h4" component="h2">
                Why List Your Property with DESCO
              </SectionTitle>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: "900px", mx: "auto", lineHeight: 1.8 }}
              >
                Our in-house teams handle the entire buyer-seller journey, so
                you don't have to hire multiple third parties. We offer full
                support regarding legal issues, property management, consulting
                and conveyancing. Our specialists will help you deal with any
                nuances that may arise not only during the transaction, but
                after closing it as well.
              </Typography>
            </Box>
          </Container>
          <HowItWorks />
          <Container>
            <PropertyImageGallery />
          </Container>

          <Container>
            <TestimonialsSection />
          </Container>
        </PageSection>

        {/* Form Section */}
        <PageSection id="list-form" sx={{ bgcolor: "#fff" }}>
          <Container>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <FormWrapper component="form" onSubmit={handleSubmit}>
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      fontWeight={600}
                    >
                      List Your Property Here
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <FormControl fullWidth required>
                          <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
                            Name
                          </FormLabel>
                          <TextField
                            name="name"
                            variant="outlined"
                            fullWidth
                            required
                            value={formData.name}
                            onChange={handleChange}
                            sx={{
                              mb: 2,
                              "& .MuiInputLabel-root": {
                                color: "black", // label color (unfocused)
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#C10E14", // label color on focus
                              },
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "black", // default border
                                },
                                "&:hover fieldset": {
                                  borderColor: "#C10E14", // hover border
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#C10E14", // border on focus
                                },
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth required>
                          <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
                            Email
                          </FormLabel>
                          <TextField
                            name="email"
                            type="email"
                            variant="outlined"
                            required
                            fullWidth
                            value={formData.email}
                            onChange={handleChange}
                            sx={{
                              mb: 2,
                              "& .MuiInputLabel-root": {
                                color: "black", // label color (unfocused)
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#C10E14", // label color on focus
                              },
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "black", // default border
                                },
                                "&:hover fieldset": {
                                  borderColor: "#C10E14", // hover border
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#C10E14", // border on focus
                                },
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth required>
                          <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
                            Phone
                          </FormLabel>
                          <TextField
                            name="phone"
                            type="phone"
                            variant="outlined"
                            required
                            fullWidth
                            value={formData.phone}
                            onChange={handleChange}
                            sx={{
                              mb: 2,
                              "& .MuiInputLabel-root": {
                                color: "black", // label color (unfocused)
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#C10E14", // label color on focus
                              },
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "black", // default border
                                },
                                "&:hover fieldset": {
                                  borderColor: "#C10E14", // hover border
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#C10E14", // border on focus
                                },
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
                            Property Type
                          </FormLabel>
                          <TextField
                            name="propertyType"
                            variant="outlined"
                            fullWidth
                            value={formData.propertyType}
                            onChange={handleChange}
                            sx={{
                              mb: 2,
                              "& .MuiInputLabel-root": {
                                color: "black", // label color (unfocused)
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#C10E14", // label color on focus
                              },
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "black", // default border
                                },
                                "&:hover fieldset": {
                                  borderColor: "#C10E14", // hover border
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#C10E14", // border on focus
                                },
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth required>
                          <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
                            Message
                          </FormLabel>
                          <TextField
                            name="message"
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.message}
                            onChange={handleChange}
                            sx={{
                              mb: 2,
                              "& .MuiInputLabel-root": {
                                color: "black", // label color (unfocused)
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#C10E14", // label color on focus
                              },
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "black", // default border
                                },
                                "&:hover fieldset": {
                                  borderColor: "#C10E14", // hover border
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#C10E14", // border on focus
                                },
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          sx={{
                            borderRadius: "50px",
                            py: 1.5,
                            fontSize: "1rem",
                            boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
                            backgroundColor: "#C10E14", // Set the background color to #C10E14
                            "&:hover": {
                              backgroundColor: "#A30B11", // Set the hover background color to #A30B11
                            },
                          }}
                        >
                          Submit Property
                        </Button>
                      </Grid>
                    </Grid>
                  </FormWrapper>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <InfoBox>
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      fontWeight={600}
                    >
                      Contact Information
                    </Typography>
                    <Box sx={{ mt: 4 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <IconWrapper>
                          <MdAddCall />
                        </IconWrapper>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            Phone
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            +971 4 576 0110
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <IconWrapper>
                          <MdEmail />
                        </IconWrapper>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            Email
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            info@descorealestate.com
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <IconWrapper>
                          <MdLocationOn />
                        </IconWrapper>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            Location
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Office 10C-08, I-Rise Tower, Barsha Heights, Dubai,
                            UAE
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="body1" paragraph>
                        Ready to take the next step? Begin with a complimentary
                        property valuation. It's fast, straightforward, and
                        packed with valuable insights.
                      </Typography>
                    </Box>
                    <PropertyImage src={image} alt="Property Valuation" />
                  </InfoBox>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </PageSection>
      </Box>
    </>
  );
};

export default ListProperty;
